import {Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField, Typography} from "@mui/material";
import {Folio} from "crqlar-react-common/dist/models/billing/folio.model";
import {useOrganization} from "crqlar-react-common/dist/state/auth.reducer";
import {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {FolioFormat} from "./folioFormat";
import {folioTotalCents} from "./folioList.util";
import {FolioActionKind, FolioActions} from "./useFolioActions";

// The confirmations as the charge dialog has always asked them (existing translation keys)
const CONFIRMATIONS: Record<Exclude<FolioActionKind, 'refund'>, {title: string, text: string}> = {
    finalize: {
        title: 'Finalize Folio',
        text: 'Are you sure you want to finalize this folio? Once finalized, no further items can be added.',
    },
    markAsPaid: {
        title: 'Mark as Paid',
        text: 'Are you sure you want to mark this folio as paid? This will change the folio status to PAID.',
    },
    markAsUncollectable: {
        title: 'Mark as Uncollectable',
        text: 'Are you sure you want to mark this folio as uncollectable? This action cannot be undone.',
    },
    void: {
        title: 'Void Folio',
        text: 'Are you sure you want to void this folio? This action cannot be undone.',
    },
    capture: {
        title: 'Capture preauthorized amount',
        text: 'Are you sure you want to capture preauthorized payment?',
    },
    release: {
        title: 'Release Funds',
        text: 'Are you sure you want to release the preauthorized funds? This will cancel the hold on the customer\'s payment method.',
    },
};

interface Props {
    actions: FolioActions
    folio?: Folio
    /** Finalizing voids the open guest link - the confirmation says so. */
    guestLinkOpen: boolean
    format: FolioFormat
}

/** The confirmation of the requested status change; a refund asks for its amount. */
const FolioActionDialogs = ({actions, folio, guestLinkOpen, format}: Props) => {
    const {t} = useTranslation();
    const organization = useOrganization();
    const maxCents = folio ? folioTotalCents(folio) : 0;
    // A folio without a currency is in the organization's
    const currency = folio?.total?.currency || folio?.currency || organization?.currencyCode || 'EUR';
    const [refundAmount, setRefundAmount] = useState(0);
    const shown = useRef<Exclude<FolioActionKind, 'refund'>>();

    // A refund starts at the whole amount
    useEffect(() => {
        if (actions.pending === 'refund') setRefundAmount(maxCents / 100);
    }, [actions.pending, maxCents]);

    const pending = actions.pending;
    if (pending === 'refund') {
        return (
            <Dialog open onClose={actions.cancel}>
                <DialogTitle>{t('Refund')}</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{mb: 2}}>
                        {t('Enter the amount to refund. The maximum refundable amount is')} {format.money(maxCents)}.
                    </DialogContentText>
                    <TextField
                        autoFocus
                        margin={'dense'}
                        label={t('Refund Amount')}
                        type={'number'}
                        fullWidth
                        value={refundAmount}
                        onChange={event => setRefundAmount(Math.max(0, Math.min(maxCents / 100, parseFloat(event.target.value) || 0)))}
                        InputProps={{
                            endAdornment: <Typography>{currency}</Typography>,
                            inputProps: {min: 0, max: maxCents / 100, step: 0.01},
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={actions.cancel} disabled={actions.running}>{t('Cancel')}</Button>
                    <Button
                        variant={'contained'}
                        disabled={refundAmount <= 0 || actions.running}
                        onClick={() => actions.confirm(Math.round(refundAmount * 100))}
                    >
                        {t('Refund')}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    // The last confirmation stays in the dialog while it fades out
    if (pending) shown.current = pending;
    const kind = shown.current;
    const confirmation = kind && CONFIRMATIONS[kind];
    return (
        <Dialog open={!!pending} onClose={actions.cancel}>
            {confirmation && (
                <>
                    <DialogTitle>{t(confirmation.title)}</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            {t(confirmation.text)}
                            {kind === 'finalize' && guestLinkOpen && ` ${t('The open guest link will be voided.')}`}
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={actions.cancel} disabled={actions.running}>{t('No')}</Button>
                        <Button onClick={() => actions.confirm()} disabled={actions.running} autoFocus>{t('Yes')}</Button>
                    </DialogActions>
                </>
            )}
        </Dialog>
    );
};

export default FolioActionDialogs;
